import { useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Lightbulb } from 'lucide-react';
import TanoCharacter from '../../components/Eleve/TanoCharacter';
import { useXp } from '../../context/XpContext';
import { getIndice } from '../../utils/coupDePouce';
import { XP_BONNE, XP_BONNE_AIDE } from '../../utils/xpConfig';
import { playHint } from '../../utils/xpSound';
import CoupDePouceSheet from './CoupDePouceSheet';

/**
 * « Coup de pouce » — FAB Tano en bas à droite de l'écran de passation. Le
 * contenu (mini-cours paginé) vit dans CoupDePouceSheet ; ce composant ne gère
 * que le déclencheur et le barème affiché.
 *
 * Le 1er clic COMPTABILISE l'aide (bonne réponse → XP réduite) ; ensuite
 * l'élève referme/rouvre à volonté, gratuitement.
 * Rendu via portail → insensible aux transforms des ancêtres (slides de carte).
 */
// xpPlein/xpAide : barème AFFICHÉ dans la note du bas — quiz prof par défaut
// (18/12), défi quotidien = barème auto (10/7) passé par Quiz.
// zIndex/showXp : par défaut = passation. L'aperçu élève côté prof les surcharge
// (empiler au-dessus de son overlay, et masquer un barème qui n'a pas cours).
function CoupDePouceButton({
    question, open, used, onOpen, onClose,
    xpPlein = XP_BONNE, xpAide = XP_BONNE_AIDE,
    zIndex = 10010, showXp = true,
}) {
    const { profil } = useXp();
    // Objet de props recréé à chaque render sinon → TanoCharacter re-render pour rien.
    const skins = useMemo(() => ({
        plume: profil?.skin_plumage,
        acc: profil?.skin_accessoire,
        aura: profil?.skin_aura,
    }), [profil?.skin_plumage, profil?.skin_accessoire, profil?.skin_aura]);

    const indice = useMemo(() => getIndice(question), [question]);

    const handleToggle = useCallback(() => {
        if (open) { onClose(); return; }
        if (!used) playHint(); // 1re ouverture = comptabilisée
        onOpen();
    }, [open, used, onOpen, onClose]);

    // ── FAB : Tano assistant, en bas à droite de l'écran ──
    const fab = createPortal(
        <button
            type="button"
            onClick={handleToggle}
            aria-label={used ? 'Revoir le coup de pouce de Tano' : 'Demander un coup de pouce à Tano'}
            title={used ? 'Revoir le coup de pouce' : 'Tano peut t\'aider ! (bonne réponse : XP réduite)'}
            // Bas-GAUCHE : à droite il se confondait avec « Valider » en mobile.
            // La calculatrice se range à sa droite via sa prop `decale`.
            className="eleve-gamified fixed flex flex-col items-center transition-transform active:scale-95"
            style={{ left: 14, bottom: 18, zIndex }}
        >
            <span
                className="cp-float relative flex items-center justify-center rounded-full shadow-xl"
                style={{
                    width: 58, height: 58,
                    background: 'radial-gradient(circle at 30% 25%, #1b3f68, #0A1F3D)',
                    border: `2.5px solid ${used ? '#29B6E8' : '#FFB938'}`,
                }}
            >
                <TanoCharacter {...skins} mood="idle" size={46} />
                <span
                    className="absolute -top-1 -right-1 flex items-center justify-center rounded-full"
                    style={{ width: 22, height: 22, background: used ? '#29B6E8' : 'linear-gradient(120deg,#FFB938,#F97316)', border: '2px solid #fff' }}
                >
                    <Lightbulb className="w-3 h-3" style={{ color: used ? '#fff' : '#3b2303' }} />
                </span>
            </span>
            <span
                className="mt-1 text-[9px] font-extrabold px-2 py-px rounded-full"
                style={{ fontFamily: 'Fredoka', background: 'rgba(10,31,61,.85)', color: used ? '#8FD6F5' : '#FFD27A', border: '1px solid rgba(255,255,255,.15)' }}
            >
                {used ? 'Revoir' : 'Aide'}
            </span>
        </button>,
        document.body,
    );

    return (
        <>
            {fab}
            {open ? (
                <CoupDePouceSheet
                    indice={indice}
                    skins={skins}
                    onClose={onClose}
                    xpPlein={xpPlein}
                    xpAide={xpAide}
                    showXp={showXp}
                    zIndex={zIndex + 2}
                />
            ) : null}
        </>
    );
}

export default CoupDePouceButton;
